import { getQueryParams, stringifyQueryParams } from 'pricing-pages-lib/utils/queryParamUtils';
import { getCheckoutInterstitialUIUrl, getPricingPageProductRoute, navigateTo } from 'pricing-pages-lib/utils/urls';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import PricingPageInterstitialBuyContactsButton from 'ui-addon-upgrades/_core/interstitial/PricingPageInterstitialBuyContactsButton';
import PricingPageInterstitialBuyNowButton from 'ui-addon-upgrades/_core/interstitial/PricingPageInterstitialBuyNowButton';
import PricingPageInterstitialBuySeatsButton from 'ui-addon-upgrades/_core/interstitial/PricingPageInterstitialBuySeatsButton';
import { BUY_ADDITIONAL_CONTACTS, BUY_ADDITIONAL_SEATS, BUY_TOUCHLESS } from 'ui-addon-upgrades/_core/purchaseMotions/PurchaseMotionTypes';
import { useDisplayedCurrencyCode } from '../../../hooks/useCurrencies';
import { useTrackingFlow } from '../../../hooks/useTrackingFlow';
import { useAppSelector } from '../../../reducers';
import { isAppAuthSelector } from '../../../selectors/AuthSelectors';
import { isUngatedForSeatsSelector } from '../../../selectors/GatingSelectors';
import { productsAndPacksForQueryParamsSelector, sortedProductConfigurationsSelector } from '../../../selectors/ProductConfigsSelectors';
import { productsFromProductInventorySelector } from '../../../selectors/ProductInventorySelectors';
import { isCoreSeatSkuSelector, isQuantifiableHubSelector, productForSkuIdSelector, skuIdIsQuantityPackSelector } from '../../../selectors/ProductsSelectors';
import { trackingProductNameSelector } from '../../../selectors/TrackingSelectors';
import { trackBuyButtonClick } from '../../../utils/tracking/trackingEventUtils';
import { useQueryParamTerm } from '../../../utils/urls/appQueryParamUtils';
import useIsEligibleForCheckoutInterstitialUI from './useIsEligibleForCheckoutInterstitialUI';
import { jsx as _jsx } from "react/jsx-runtime";
const ToInterstitialPurchaseMotionButtonWrapper = props => {
  var _props$purchaseMotion;
  const {
    nonMerchandiseIdIdentifier,
    productConfigurations = [],
    queryParamTerm: termOverride,
    trackingSource
  } = props;
  const sortedProductConfigurations = useAppSelector(state => sortedProductConfigurationsSelector(state, {
    productConfigurations
  }));
  const productConfiguration = sortedProductConfigurations[0];
  const product = useAppSelector(state => productForSkuIdSelector(state, {
    skuId: productConfiguration.skuId
  }));
  const {
    apiName
  } = product;
  const history = useHistory();
  const {
    queryParamTerm
  } = useQueryParamTerm();
  const purchaseMotion = (_props$purchaseMotion = props.purchaseMotion) !== null && _props$purchaseMotion !== void 0 ? _props$purchaseMotion : props.purchaseMotions[props.level - 1];
  const isAppAuth = useAppSelector(isAppAuthSelector);
  const trackingProductNames = useSelector(state => productConfigurations.map(({
    skuId
  }) => trackingProductNameSelector(state, {
    skuId
  })));
  const ownedProducts = useSelector(state => productsFromProductInventorySelector(state)).map(p => p.apiName);
  const filteredProductConfigurations = useAppSelector(state => {
    // filter out non-seats related product configurations
    if (purchaseMotion === BUY_ADDITIONAL_SEATS && isUngatedForSeatsSelector(state)) {
      return productConfigurations.filter(config => isCoreSeatSkuSelector(state, {
        skuId: config.skuId
      }) || isQuantifiableHubSelector(state, {
        skuId: config.skuId
      }) || skuIdIsQuantityPackSelector(state, {
        skuId: config.skuId
      }) && config.quantity > 0);
    }
    return productConfigurations;
  });

  // convert the product configurations to query params
  const productConfigurationsAsQueryParams = useAppSelector(state => productsAndPacksForQueryParamsSelector(state, {
    productConfigurations: filteredProductConfigurations
  }));
  const {
    isEligibleForCheckoutInterstitialUI,
    targetFlow
  } = useIsEligibleForCheckoutInterstitialUI(filteredProductConfigurations);
  const seleniumId = nonMerchandiseIdIdentifier || apiName;
  const propsToPass = Object.assign({}, props, {
    seleniumId
  });
  const flow = useTrackingFlow({
    productConfigurations,
    purchaseMotion
  });
  const currencyCode = useDisplayedCurrencyCode();
  const onClick = () => {
    if (props.onClick) {
      props.onClick();
    }
    const shouldNavigateToCheckoutInterstitialUI = isEligibleForCheckoutInterstitialUI && targetFlow;
    const queryParams = getQueryParams();
    const pricingPageRoute = getPricingPageProductRoute(isAppAuth);
    const stringifiedParams = stringifyQueryParams(Object.assign({}, queryParams, productConfigurationsAsQueryParams, {
      currencyCode,
      pricingPageProductRoute: pricingPageRoute || '',
      purchaseMotion,
      term: termOverride || queryParamTerm
    }));
    trackBuyButtonClick({
      purchaseMotion,
      productNames: trackingProductNames,
      ownedProducts,
      trackingSource: props.trackingSource,
      screen: props.screen,
      flow
    });
    if (shouldNavigateToCheckoutInterstitialUI) {
      navigateTo(getCheckoutInterstitialUIUrl(stringifiedParams, targetFlow));
      return;
    }
    history.push(`/interstitial?${stringifiedParams}`);
  };
  if (purchaseMotion === BUY_TOUCHLESS) {
    return /*#__PURE__*/_jsx(PricingPageInterstitialBuyNowButton, Object.assign({}, propsToPass, {
      "data-test-id": `${trackingSource}-${seleniumId}-interstitial-buyTouchless-button`,
      onClick: onClick
    }));
  }
  if (purchaseMotion === BUY_ADDITIONAL_CONTACTS) {
    return /*#__PURE__*/_jsx(PricingPageInterstitialBuyContactsButton, Object.assign({}, propsToPass, {
      "data-test-id": `${trackingSource}-${seleniumId}-interstitial-confirm-contacts-button`,
      onClick: onClick
    }));
  }
  if (purchaseMotion === BUY_ADDITIONAL_SEATS) {
    return /*#__PURE__*/_jsx(PricingPageInterstitialBuySeatsButton, Object.assign({}, propsToPass, {
      "data-test-id": `${trackingSource}-${seleniumId}-interstitial-add-additional-seats-button`,
      onClick: onClick
    }));
  }
  return null;
};
export default ToInterstitialPurchaseMotionButtonWrapper;